import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { IRemunerationBonus } from 'interfaces/RemunerationBonus';
import {
  IMyKnownError,
  REQUEST_LIFE_CICLE,
  ICreateRequestPayload,
} from 'interfaces/requests';

import { get, post, patch, MICROSERVICES } from 'api';
import { AppDispatch } from 'store';
import { normalizeAndRemoveFields } from 'utils/files';
import { openAlert } from 'modules/alertHandler';

import endpoints from './endpoints';
import { IRemunerationBonusState } from './interfaces';
import { setSelectedRemunerationModeAction } from 'modules/remunerationModeNavigation/actions';
import { listRemunerationBonusAction } from 'modules/remunerationBonusList';
import { REMUNERATION_FORM_MODE } from 'modules/remunerationModeNavigation/constants';
import { parseToInt } from 'utils/texts';
import { remunerationFieldsToRemove } from 'utils/remuneration';

export const showRemunerationBonusAction = createAsyncThunk<
  IRemunerationBonus,
  string,
  { rejectValue: IMyKnownError }
>('showRemunerationBonus', async (payload, { rejectWithValue, dispatch }) => {
  try {
    const result = await get<IRemunerationBonus>(MICROSERVICES.STRATEGIES, {
      url: endpoints.showRemunerationBonus(payload),
    });
    const flows = result.data.flows.map((flow) => ({
      ...flow,
      flowRanges: flow.flowRanges.map((flowRange, index) => ({
        ...flowRange,
        id: index.toString(),
      })),
    }));
    const remuneration = {
      ...result.data,
      id: result.data._id,
      flows: flows,
    };
    dispatch(
      setSelectedRemunerationModeAction(REMUNERATION_FORM_MODE.viewBonus),
    );
    return remuneration;
  } catch (error) {
    dispatch(
      openAlert({
        title: 'Erro!',
        content: 'Erro ao visualizar a Remuneração selecionada!',
      }),
    );
    return rejectWithValue(error.response.data.message);
  }
});

export const createRemunerationBonusAction = createAsyncThunk<
  void,
  ICreateRequestPayload<IRemunerationBonus>,
  { rejectValue: IMyKnownError; dispatch: AppDispatch }
>(
  'createRemunerationBonus',
  async ({ item, resetFormSubmitting }, { rejectWithValue, dispatch }) => {
    const flows = item.flows.map((flow) => {
      if (flow.system === 'all') {
        delete flow.system;
      }
      return Object.assign({}, flow, {
        flowRanges: flow.flowRanges.map((flowRange) =>
          normalizeAndRemoveFields(Object.assign({}, flowRange), [
            'id',
            'beforeBreakpointValue',
          ]),
        ),
      });
    });

    const periodicityMonthSpecification = parseToInt(
      item.periodicityMonthSpecification,
    );
    const parsedRemunerationBonus = {
      ...normalizeAndRemoveFields(
        Object.assign({}, { ...item, periodicityMonthSpecification }),
        remunerationFieldsToRemove(item.periodicityType),
      ),
      flows,
    };
    try {
      await post<IRemunerationBonus>(MICROSERVICES.STRATEGIES, {
        url: endpoints.createRemunerationsBonus(),
        data: parsedRemunerationBonus,
      });
      resetFormSubmitting();
      dispatch(
        openAlert({
          title: 'Sucesso!',
          content: 'Fluxo de Remuneração criado com sucesso!',
          primaryButtonAction: setSelectedRemunerationModeAction(
            REMUNERATION_FORM_MODE.list,
          ),
        }),
      );
    } catch (error) {
      const errorMessage = error.response.data.message.match('name is already')
        ? 'Já existe Remuneração Bônus com esse nome.'
        : 'Erro para criar uma Remuneração Bônus';
      dispatch(
        openAlert({
          title: 'Erro!',
          content: errorMessage,
        }),
      );
      return rejectWithValue(error.response.data.message);
    }
  },
);

export const inactivateRemunerationBonusAction = createAsyncThunk<
  void,
  string,
  { rejectValue: IMyKnownError }
>(
  'inactivateRemunerationBonus',
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      await patch<unknown>(MICROSERVICES.STRATEGIES, {
        url: endpoints.inactivateRemunerationBonus(payload),
      });
      dispatch(
        openAlert({
          title: 'Sucesso!',
          content: 'Fluxo de Remuneração inativado com sucesso!',
          primaryButtonAction: listRemunerationBonusAction({}),
        }),
      );
    } catch (error) {
      dispatch(
        openAlert({
          title: 'Erro!',
          content: 'Erro ao inativar a Remuneração selecionada!',
        }),
      );
      return rejectWithValue(error.response.data.message);
    }
  },
);

const initialState: IRemunerationBonusState = {
  loading: REQUEST_LIFE_CICLE.IDLE,
  submitting: REQUEST_LIFE_CICLE.IDLE,
  remunerationBonus: {} as IRemunerationBonus,
  errorMessage: '',
};

const remunerationBonusSlice = createSlice({
  name: 'remunerationBonus',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(showRemunerationBonusAction.fulfilled, (state, action) => {
      state.errorMessage = '';
      state.remunerationBonus = action.payload;
      state.loading = REQUEST_LIFE_CICLE.SUCCEEDED;
      return state;
    });
    builder.addCase(showRemunerationBonusAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
      return state;
    });
    builder.addCase(showRemunerationBonusAction.rejected, (state, action) => {
      state.loading = REQUEST_LIFE_CICLE.FAILED;
      state.errorMessage = action.payload?.message || '';
      return state;
    });
    builder.addCase(createRemunerationBonusAction.fulfilled, (state) => {
      state.submitting = REQUEST_LIFE_CICLE.SUCCEEDED;
      return state;
    });
    builder.addCase(createRemunerationBonusAction.pending, (state) => {
      state.submitting = REQUEST_LIFE_CICLE.PENDING;
      return state;
    });
    builder.addCase(createRemunerationBonusAction.rejected, (state, action) => {
      state.submitting = REQUEST_LIFE_CICLE.FAILED;
      state.errorMessage = action.payload?.message || '';
      return state;
    });
    builder.addCase(inactivateRemunerationBonusAction.fulfilled, () => {
      return initialState;
    });
    builder.addCase(inactivateRemunerationBonusAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
      return state;
    });
    builder.addCase(
      inactivateRemunerationBonusAction.rejected,
      (state, action) => {
        state.loading = REQUEST_LIFE_CICLE.FAILED;
        state.errorMessage = action.payload?.message || '';
        return state;
      },
    );
  },
});

const { reducer } = remunerationBonusSlice;
export default reducer;
